// Pure column auto-mapping for the Excel importer.

/** "Fecha de Vencimiento" -> "fechadevencimiento": accents, case and punctuation removed. */
export const normalizeHeader = (value) =>
	String(value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036F]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

/**
 * Candidate names for one field, grouped by priority level:
 *   0 the deployment's terminology label (custom override wins),
 *   1 the base label, 2 the field key, 3 aliases,
 *   4/5 labels of a reference's lookup options (e.g. "Padrón" for the asset column).
 * resolveLabel(key) is the terminology lookup (getLabel / t), baseLabels is BASE_LABELS.
 */
export const fieldCandidates = (field, reference, resolveLabel, baseLabels) => {
	const levels = [
		[resolveLabel(field.labelKey)],
		[baseLabels[field.labelKey]],
		[field.key],
		field.aliases || [],
		(reference?.lookupOptions || []).map((option) => resolveLabel(option.labelKey)),
		(reference?.lookupOptions || []).map((option) => baseLabels[option.labelKey]),
	];
	return levels.map((names) => [...new Set(names.filter(Boolean).map(normalizeHeader).filter(Boolean))]);
};

const MIN_PARTIAL = 4;

/**
 * Map Excel headers to fields, one-to-one. Exact matches are tried level by
 * level across ALL fields first (so a custom label beats another field's
 * alias), and only then partial "contains" matches, as a fallback.
 * Returns { [fieldKey]: header }.
 */
export const autoMap = (headers, fields, candidatesFor) => {
	const normalizedHeaders = headers.map((header) => ({ header, norm: normalizeHeader(header) }));
	const used = new Set();
	const mapping = {};
	const candidates = Object.fromEntries(fields.map((field) => [field.key, candidatesFor(field)]));
	const levelCount = Math.max(0, ...Object.values(candidates).map((levels) => levels.length));

	for (let level = 0; level < levelCount; level += 1) {
		for (const field of fields) {
			if (mapping[field.key]) continue;
			const names = candidates[field.key][level] || [];
			const match = normalizedHeaders.find((h) => !used.has(h.header) && names.includes(h.norm));
			if (match) {
				mapping[field.key] = match.header;
				used.add(match.header);
			}
		}
	}

	for (let level = 0; level < levelCount; level += 1) {
		for (const field of fields) {
			if (mapping[field.key]) continue;
			const names = (candidates[field.key][level] || []).filter((name) => name.length >= MIN_PARTIAL);
			const match = normalizedHeaders.find(
				(h) =>
					!used.has(h.header) &&
					h.norm.length >= MIN_PARTIAL &&
					names.some((name) => h.norm.includes(name) || name.includes(h.norm)),
			);
			if (match) {
				mapping[field.key] = match.header;
				used.add(match.header);
			}
		}
	}
	return mapping;
};

/** Assign `header` to `fieldKey`, clearing it from any other field (one-to-one). */
export const assignColumn = (mapping, fieldKey, header) => {
	const next = {};
	Object.entries(mapping).forEach(([key, value]) => {
		if (key !== fieldKey && value !== header) next[key] = value;
	});
	if (header) next[fieldKey] = header;
	return next;
};

// Header words that reveal which parent field a reference column holds
const LOOKUP_HINTS = [
	{ words: ['padron', 'lot'], keys: ['lot', 'assetLot'] },
	{ words: ['rut'], keys: ['rut', 'accountRut'] },
	{ words: ['cedula', 'documento', 'nrodoc', 'ci'], keys: ['nroDocumento', 'accountDoc'] },
	{ words: ['mail', 'correo'], keys: ['email'] },
	{ words: ['activo', 'propiedad', 'inmueble', 'unidad'], keys: ['name', 'assetName'] },
	{ words: ['customid', 'id'], keys: ['customId', 'internalId'] },
];

/**
 * Default lookup option for a reference column from its header: a header
 * equal to an option's label ("Padrón") picks it; otherwise the option's own
 * `hints` ("Corresponde a" -> free text), then generic keyword hints;
 * otherwise the reference's default.
 */
export const guessLookup = (header, reference, resolveLabel, baseLabels) => {
	const options = reference?.lookupOptions || [];
	if (!header || options.length === 0) return reference?.defaultLookup || options[0]?.key;
	const norm = normalizeHeader(header);
	const exact = options.find(
		(option) =>
			normalizeHeader(resolveLabel(option.labelKey)) === norm || normalizeHeader(baseLabels[option.labelKey]) === norm,
	);
	if (exact) return exact.key;
	// Hints declared on the schema's own options win over the generic keywords
	const hinted = options.find((option) => (option.hints || []).some((hint) => norm.includes(normalizeHeader(hint))));
	if (hinted) return hinted.key;
	for (const hint of LOOKUP_HINTS) {
		const matchesWord = hint.words.some((word) => (word.length <= 2 ? norm === word : norm.includes(word)));
		if (!matchesWord) continue;
		const option = options.find((o) => hint.keys.includes(o.key));
		if (option) return option.key;
	}
	return reference.defaultLookup || options[0].key;
};

const storageKey = (entity) => `importMapping:${entity}`;

/** Last mapping used for this entity, only for headers present in the current file. */
export const loadSavedMapping = (entity, headers) => {
	try {
		const saved = JSON.parse(localStorage.getItem(storageKey(entity)) || '{}');
		const present = new Set(headers);
		return Object.fromEntries(Object.entries(saved).filter(([, header]) => present.has(header)));
	} catch {
		return {};
	}
};

export const saveMapping = (entity, mapping) => {
	try {
		localStorage.setItem(storageKey(entity), JSON.stringify(mapping));
	} catch {
		// Private mode / blocked storage: remembering the mapping is only a convenience
	}
};

/**
 * Label key explaining how an unmapped field still gets a value (schema
 * `fallbackLabelKey`, e.g. Cuenta asociada -> the asset's owner), or null.
 * `fallbackFrom` limits it to when that other field has a column
 * (Vencimiento is built from "Día de vto" + the chosen month).
 */
export const columnFallback = (field, mapping) => {
	if (!field.fallbackLabelKey || mapping[field.key]) return null;
	if (field.fallbackFrom && !mapping[field.fallbackFrom]) return null;
	return field.fallbackLabelKey;
};

const picksKey = (entity) => `importPicks:${entity}`;

/** Key a file value is remembered under: "Casa Carrasco 26 " and "casa carrasco 26" are the same value. */
export const pickKey = (value) => normalizeHeader(value);

/**
 * Records the user picked for file values that matched nothing, per reference
 * field: { [fieldKey]: { [pickKey(value)]: { id, label } } }. Remembered so the
 * next file with the same "Corresponde a" text resolves on its own.
 */
export const loadPicks = (entity) => {
	try {
		const saved = JSON.parse(localStorage.getItem(picksKey(entity)) || '{}');
		return saved && typeof saved === 'object' ? saved : {};
	} catch {
		return {};
	}
};

export const savePicks = (entity, picks) => {
	try {
		localStorage.setItem(picksKey(entity), JSON.stringify(picks));
	} catch {
		// Private mode / blocked storage: remembering picks is only a convenience
	}
};
